import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useHasPermission } from '@/entities/profile';
import { User, useCreateUser, usePasswordRulesField, useUpdateUser } from '@/entities/users';
import { useAppNavigate } from '@/shared/lib';
import { UserFormValues } from './types';
import { useUserSchema } from './validation';
import { useFormGuardActions } from '@/entities/formGuard';

type Params = {
  initial?: User;
  onSuccess?: () => void;
};

export const useUserForm = (params: Params) => {
  const { initial, onSuccess } = params;
  const navigate = useAppNavigate();
  const allowAdmin = useHasPermission('ADMIN_USER');
  // Вид карточки задаёт только открытая запись; право управляет активностью полей и составом разделов
  const isEdit = !!initial;
  const schema = useUserSchema(!isEdit);
  const { resetFormGuard } = useFormGuardActions();

  const defaults: Partial<UserFormValues> = useMemo(
    () => ({
      firstName: initial?.firstName ?? '',
      lastName: initial?.lastName ?? '',
      middleName: initial?.middleName ?? '',
      email: initial?.email ?? '',
      roleCode: initial?.role?.code ?? '',
      isActive: initial?.isActive ?? true,
      // Состав показывается из данных самой карточки: справочник групп его не дополняет
      userGroups: initial?.userGroups?.map((group) => ({ id: group.id, name: group.name })) ?? [],
      password: '',
    }),
    [initial],
  );

  const form = useForm<UserFormValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    form.reset(defaults);
  }, [form, defaults]);

  // Правила пароля до отправки проверяет схема карточки, здесь — только сообщения поля
  const { showServerRefusal: showPasswordRefusal } = usePasswordRulesField({
    control: form.control,
    setError: form.setError,
    clearErrors: form.clearErrors,
    name: 'password',
  });

  const { mutateAsync: createUser, isPending: isCreatingPending } = useCreateUser();
  const { mutateAsync: updateUser, isPending: isUpdating } = useUpdateUser();

  const isDisabled = !allowAdmin;
  const isLoading = isCreatingPending || isUpdating;

  const onSubmit = form.handleSubmit(async (values) => {
    const userGroups = (values.userGroups ?? []).map((group) => ({ id: group.id }));
    const { password } = values;

    if (isEdit && initial) {
      try {
        await updateUser({
          id: initial.id,
          firstName: values.firstName,
          lastName: values.lastName,
          middleName: values.middleName || null,
          email: values.email,
          role: values.roleCode ? { code: values.roleCode } : null,
          isActive: values.isActive,
          userGroups,
        });
        resetFormGuard();
      } catch {
        // Причину отказа (например, занятый email) показало общее уведомление клиента;
        // карточка остаётся с введёнными данными и несохранёнными изменениями
        return;
      }
    } else if (password) {
      try {
        const created = await createUser({
          firstName: values.firstName,
          lastName: values.lastName,
          middleName: values.middleName || null,
          email: values.email,
          role: { code: values.roleCode },
          isActive: values.isActive,
          password,
          userGroups,
        });
        resetFormGuard();
        navigate('/users/lists/:id', { id: created.id.toString() }, { replace: true });
      } catch (error) {
        // Карточка остаётся с введёнными данными и несохранёнными изменениями
        showPasswordRefusal(error);
        return;
      }
    }

    onSuccess?.();
  });

  // Handle title (name) change on blur - save immediately if editing
  const handleTitleSave = async (value: string) => {
    form.setValue('firstName', value);

    if (isEdit && initial && value !== initial.firstName) {
      await updateUser({
        id: initial.id,
        firstName: value,
      });
    }
  };

  return {
    defaultValues: defaults,
    form,
    isEdit,
    isLoading,
    isDisabled,
    onSubmit,
    handleTitleSave,
  } as const;
};
